<template>
  <div class="warranty-info">
    <Loader
      v-if="loaderEnabled"
      :logo="loaderLogo"
    ></Loader>
    <KTCodePreview v-bind:title="title">
      <template v-slot:toolbar>
        <b-button
          v-if="formModel.imeiCode"
          size="sm"
          variant="outline-primary"
          @click="onShowWarrantyImeiHistory"
          class="mr-2"
        >
          <i class="fa fa-history mr-1"></i>
          Lịch sử bảo hành
        </b-button>
        <b-button
          v-if="formModel.id"
          size="sm"
          variant="outline-primary"
          @click="showHistoryModal = true"
        >
          <i class="fa fa-history mr-1"></i>
          Lịch sử chỉnh sửa
        </b-button>
      </template>
      <template v-slot:preview>
        <LoadingSpinner
          v-if="isLoadingComponent"
          :message="'Đang tải dữ liệu...'"
        />
        <template v-else>
          <b-row class="col-md-12 mb-6 m-0 p-0">
            <WarrantyCodeHeader
              :code="formModel.code"
              :bill-number="formModel.billNumber"
              :created-at="formModel.createdAt"
              :previous-store-name="formModel.previousStoreName"
            />
          </b-row>
          <!-- Dispatch section (service + payment + upload) -->
          <WarrantyDispatchSection
            v-model="formModel"
            :warranty-repairs.sync="warrantyRepairs"
            :disable-replace-export-button="isViewOnly"
            :warranty-service-options="warrantyServiceOptions"
            :accompanying-equipments="accompanyingEquipments"
            :option-stores="optionStores"
            :inspected-by-employee-name="inspectedByEmployeeName"
            :solution-options="solutionOptions"
            :severity-level-options="severityLevelOptions"
            :error-group-options="errorGroupOptions"
            :is-edit-mode="isEditMode"
            :can-update-service-type="canUpdateWarrantyServiceType"
            @employee-selected="onInspectedByEmployeeSelected"
            @refresh="onRefresh"
            @save="saveWarranty"
          />
          <hr
            class="hr-text"
            data-content="Phiên sửa chữa"
            style="font-weight: 600"
          />
          <WarrantyRepairSection
            v-model="formModel"
            :warranty-repairs.sync="warrantyRepairs"
            :disable-replace-export-button="isViewOnly"
            :solution-options="solutionOptions"
            :severity-level-options="severityLevelOptions"
            :error-group-options="errorGroupOptions"
          />
        </template>
      </template>
      <template v-slot:foot>
        <SaveButton
          v-if="(checkPermission('WARRANTY_UPDATE') || checkPermission('WARRANTY_UPDATE_UNLIMIT')) && !isViewOnly"
          :is-busy="isLoading || isLoadingComponent"
          text="Lưu"
          variant="primary"
          size="sm"
          button-class="font-weight-bolder mr-4"
          @click="saveWarranty"
        >
          <i class="fas fa-save mr-1"></i>
          Lưu
        </SaveButton>
        <b-button
          v-if="isShowCompleteButton"
          :disabled="isLoading || isLoadingComponent"
          size="sm"
          variant="success"
          class="font-weight-bolder mr-4"
          @click="onCompleteWarranty"
        >
          <i class="fas fa-check-circle mr-1"></i>
          Hoàn thành
        </b-button>
        <b-button
          v-if="formModel.id && isShowOutsourceButton"
          :disabled="isLoading || isLoadingComponent"
          size="sm"
          variant="warning"
          class="mr-2"
          @click="openOutsourceForm"
        >
          <i class="fas fa-truck-loading mr-1"></i>
          Chuyển Outsource
        </b-button>
        <b-button
          size="sm"
          variant="secondary"
          :disabled="isLoading"
          class="font-weight-bolder"
          @click="cancel"
        >
          <i class="fas fa-arrow-left mr-1"></i>
          Hủy
        </b-button>
        </template>
    </KTCodePreview>
    <WarrantyHistoryModal
      v-model="showHistoryModal"
      :warranty-id="formModel.id"
    />
    <OutsourceFormModal
      ref="outsourceFormRef"
      :warranty="warrantyWithRepairs"
      @created="onOutsourceCreated"
    />
    <template>
      <b-modal
        ref="modal-warranty-imei-history"
        hide-footer
        title="Lịch sử bảo hành"
        id="modal-warranty-imei-history"
        size="xl"
      >
        <WarrantyImeiHistory :imeiCode="formModel.imeiCode" />
      </b-modal>
    </template>
    
    <ModalConfirmCompleteWarranty
      ref="modalConfirmComplete"
      :warranty="warrantyWithRepairs"
      @confirm="onConfirmCompleteWarranty"
    />
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue'
import { SET_BREADCRUMB } from '@/core/services/store//modules/breadcrumbs.module'
import { mapGetters } from 'vuex'
import Loader from '@/view/content/Loader.vue'
import timeUtils from '@/utils/date'
import {
  getWarrantyById,
  updateDispatch,
  getAccompanyingEquipments,
  getWarrantyServices,
  getAllSolutions,
  getErrorGroups,
  getErrorSeverities,
  getReplaceProductsByWarrantyId,
} from './utils/warrantyApi'
import { checkHasActiveOutsource } from './outsource/warrantyOutsourceApi'
import { handleError, currencyMask, unMaskPrice, makeToastFaile } from '@/utils/common'
import WarrantyHistoryModal from './components/WarrantyHistoryModal.vue'
import {
  formatDBDate,
  DATETIME_DB_FORMAT,
  CLIENT_DATE_TIME_NO_SECONDS,
  convertToDateSlash,
  DATE_FORMAT,
} from '@/utils/date'
import { PAYMENT_TYPE, WARRANTY_RECEPTION_STATUS } from '@/utils/enum'
import permissionMixin from '@/view/mixins/permission.mixin'
import apiCommonMixin from '@/view/mixins/api-common.mixins'
import WarrantyDispatchSection from './components/WarrantyDispatchSection.vue'
import WarrantyRepairSection from './components/WarrantyRepairSection.vue'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import WarrantyCodeHeader from './components/WarrantyCodeHeader.vue'
import WarrantyImeiHistory from './components/ImeiWarrantyHistory.vue'
import OutsourceFormModal from './outsource/components/OutsourceFormModal.vue'
import ModalConfirmCompleteWarranty from './components/ModalConfirmCompleteWarranty.vue'
import {
  buildWarrantyRepairsPayload,
  normalizeWarrantyRepairFromApi,
} from './utils/warrantyRepairHelpers'

export default {
  mixins: [permissionMixin, apiCommonMixin],
  props: {
    warrantyId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      title: 'Chi tiết phiếu điều phối',
      isLoadingComponent: false,
      isLoading: false,
      showHistoryModal: false,
      warrantyRepairs: [{}],
      formModel: {
        id: null,
        code: '',
        warrantyServiceId: null,
        warrantyServiceName: '',
        customerId: null,
        customerName: '',
        storeId: null,
        storeName: '',
        previousStoreName: null,
        status: null,
        customerPhone: '',
        customerContactNumber: '',
        customerAddress: '',
        customerEmail: '',
        productId: '',
        productName: '',
        imeiCode: '',
        purchaseDate: null,
        inspectedByEmployeeId: null,
        expectedReturnDate: null,
        estimatedCost: null,
        actualCost: null,
        supportCost: null,
        repairCost: null,
        issueDescription: '',
        externalNote: '',
        internalNote: '',
        receiptNumber: '',
        trackingNumber: '',
        accompanyingItems: [],
        // Repair fields
        errorGroupId: null,
        technicalErrorId: null,
        technicalErrorIds: null,
        technicalErrors: [],
        errorCode: '',
        errorSeverityId: null,
        primaryTechnicianId: null,
        supportTechnician1Id: null,
        supportTechnician2Id: null,
        startAt: null,
        estimatedCompletionAt: null,
        actualCompletionAt: null,
        solutionId: null,
        repairSolution: '',
        repairMethod: '',
        isCannotRepair: false,
        isDisagreePrice: false,
        returnCount: 0,
        newProductId: null,
        newImeiId: null,
        newProductName: null,
        newProductPrice: null,
        newImeiCode: null,
        replaceProducts: [],
        paymentInfo: {
          cashAmount: 0,
          cashAccountantName: '',
          cashAccountId: null,
          transferAmount: 0,
          transferAccountantName: '',
          transferAccountantId: null,
          transferReferenceCode: '',
          creditAmount: 0,
          creditAccountantName: '',
          creditAccountantId: null,
          creditCode: '',
          creditCardNo: '',
          creditCardFee: 0,
          creditFeeAccountantName: '',
          creditFeeAccountantId: null,
          payCreditFeeType: 1,
          installedMoneyAmount: 0,
          installmentCode: '',
          installmentAccountName: '',
          installMoneyAccountId: null,
          customerIndentifyNo: '',
          installmentProgramMonthNo: 0,
          installmentProgramPrepayPercent: 0,
          refundAmount: 0,
          refundOptionId: null,
          refundAccountId: null,
          refundAccountCode: null,
        },
        installments: [],
        credits: [],
        transfer: [],
        cash: [],
        refunds: [],
        deposit: [],
        depositAmount: null,
        warrantyProduct: {},
        warrantyProductId: null,
      },
      accompanyingEquipments: [],
      warrantyServiceOptions: [],
      inspectedByEmployeeName: '',
      optionStores: [],
      solutionOptions: [],
      errorGroupOptions: [],
      technicalErrorOptions: [],
      severityLevelOptions: [],
      mask: currencyMask,
      dpConfigs: {
        date: timeUtils.DP_CONFIG.date,
        datetime: timeUtils.DP_CONFIG_DATETIME.date,
      },
    }
  },
  components: {
    KTCodePreview,
    Loader,
    WarrantyDispatchSection,
    WarrantyRepairSection,
    LoadingSpinner,
    WarrantyCodeHeader,
    OutsourceFormModal,
    WarrantyHistoryModal,
    WarrantyImeiHistory,
    ModalConfirmCompleteWarranty,
  },
  computed: {
    ...mapGetters(['breadcrumbs', 'pageTitle', 'layoutConfig']),
    warrantyWithRepairs() {
      return { ...this.formModel, warrantyRepairs: this.warrantyRepairs }
    },
    isShowCompleteButton() {
      return (
        this.checkPermission('WARRANTY_COMPLETED') &&
        ![WARRANTY_RECEPTION_STATUS.COMPLETED, WARRANTY_RECEPTION_STATUS.RETURNED].includes(
          this.formModel.status,
        )
      )
    },
    loaderEnabled() {
      return !/false/.test(this.layoutConfig('loader.type'))
    },
    loaderLogo() {
      return process.env.BASE_URL + this.layoutConfig('loader.logo')
    },
    isEditMode() {
      return !!this.$route.query.id || !!this.warrantyId
    },
    isShowOutsourceButton() {
      return ![WARRANTY_RECEPTION_STATUS.COMPLETED, WARRANTY_RECEPTION_STATUS.RETURNED].includes(
        this.formModel.status,
      )
    },
    isViewOnly() {
      return (
        this.$route.query.action === 'view' &&
        [WARRANTY_RECEPTION_STATUS.RETURNED, WARRANTY_RECEPTION_STATUS.COMPLETED].includes(
          this.formModel.status,
        ) &&
        !this.checkPermission('WARRANTY_UPDATE_UNLIMIT')
      )
    },
    canUpdateWarrantyServiceType() {
      return (
        this.checkPermission('WARRANTY_UPDATE_SERVICE_TYPE') ||
        this.checkPermission('WARRANTY_UPDATE_UNLIMIT')
      )
    },
  },
  watch: {
    warrantyId: {
      handler(id) {
        if (id) this.getWarrantyInfo()
      },
      immediate: false,
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: this.title }])
    this.fetchAccompanyingEquipments()
    this.fetchWarrantyServices()
    this.fetchStores()
    this.fetchSolutions()
    this.fetchErrorGroups()
    this.fetchErrorSeverities()
  },
  created() {
    if (this.isEditMode) {
      this.getWarrantyInfo()
    }
  },
  methods: {
    buildWarrantyRepairsPayload() {
      return buildWarrantyRepairsPayload(this.warrantyRepairs)
    },
    onShowWarrantyImeiHistory() {
      this.$refs['modal-warranty-imei-history'].show()
    },
    onInspectedByEmployeeSelected(employee) {
      if (employee) {
        this.inspectedByEmployeeName = employee.name
      } else {
        this.inspectedByEmployeeName = ''
      }
    },
    onRefresh() {
      this.getWarrantyInfo()
    },
    async fetchSolutions() {
      try {
        this.solutionOptions = await getAllSolutions()
      } catch (error) {
        handleError(error)
      }
    },
    async fetchErrorGroups() {
      try {
        const groups = await getErrorGroups()
        this.errorGroupOptions = [
          { value: null, text: '--Chọn phân loại--' },
          ...groups.map((group) => ({
            value: group.id,
            text: `${group.code} - ${group.name}`,
          })),
        ]
      } catch (error) {
        handleError(error)
      }
    },
    async fetchErrorSeverities() {
      try {
        const severities = await getErrorSeverities()
        this.severityLevelOptions = [
          { value: null, text: '--Chọn mức độ--' },
          ...severities.map((severity) => ({
            value: severity.code || severity.id,
            text: severity.name,
          })),
        ]
      } catch (error) {
        handleError(error)
      }
    },
    async fetchAccompanyingEquipments() {
      try {
        this.accompanyingEquipments = await getAccompanyingEquipments()
      } catch (error) {
        handleError(error)
      }
    },
    async fetchWarrantyServices() {
      try {
        const services = await getWarrantyServices()
        // Filter only active services
        this.warrantyServiceOptions = services.filter((service) => service.isActive === 1)
      } catch (error) {
        handleError(error)
      }
    },
    async fetchStores() {
      try {
        const { stores, currentStoreId, currentStoreName } = await this.fetchStoreByUser()
        this.optionStores = this.mapDataTreeSelect(stores, ['id', 'name', 'shortName'])
        // Chỉ auto-chọn store khi tạo mới; edit mode giữ storeId từ API
        if (currentStoreId && !this.formModel.id) {
          this.formModel.storeId = currentStoreId
          this.formModel.storeName = currentStoreName
        }
      } catch (error) {
        handleError(error)
      }
    },
    mapDataTreeSelect(items = [], keys = []) {
      if (!items || items.length === 0) return []
      const [idKey, nameKey, shortNameKey] = keys
      return items.map((item) => ({
        id: item[idKey],
        label: item[nameKey] || item[shortNameKey],
        shortName: item[shortNameKey] || item[nameKey],
      }))
    },
    async fetchReplaceProducts() {
      if (!this.formModel.id) {
        this.formModel.replaceProducts = []
        return
      }
      this.formModel.replaceProducts = await getReplaceProductsByWarrantyId(this.formModel.id)
    },
    async getWarrantyInfo() {
      const id = this.warrantyId || this.$route.query.id
      if (!id) return
      this.isLoadingComponent = true
      try {
        const result = await getWarrantyById(id)
        if (result) {
          const accompanyingItemIds = (result.accompanyingEquipments || []).map(
            ({ accompanyingEquipmentId }) => accompanyingEquipmentId,
          )
          this.formModel = {
            id: result.id,
            code: result.code,
            warrantyServiceId: result.warrantyServiceId,
            warrantyServiceName: result.warrantyServiceName,
            customerId: result.customerId,
            customerName: result.customerName,
            status: result.status,
            storeId: result.storeId,
            storeName: result.storeName,
            customerPhone: result.customerPhone,
            customerContactNumber: result.customerContactNumber,
            customerAddress: result.customerAddress,
            customerEmail: result.customerEmail,
            productId: result.productId,
            productName: result.productName,
            imeiCode: result.imeiCode,
            inspectedByEmployeeId: result.inspectedByEmployeeId,
            inspectedByEmployeeName: result.inspectedByEmployeeName,
            estimatedCost: result.estimatedCost,
            actualCost: result.actualCost,
            supportCost: result.supportCost ?? null,
            depositAmount: result.depositAmount ?? null,
            issueDescription: result.issueDescription,
            externalNote: result.externalNote,
            internalNote: result.internalNote,
            receiptNumber: result.receiptNumber,
            trackingNumber: result.trackingNumber,
            accompanyingItems: accompanyingItemIds,
            expectedReturnDate: convertToDateSlash(result.expectedReturnDate),
            purchaseDate: convertToDateSlash(result.purchaseDate, DATE_FORMAT),
            returnCount: result.returnCount,
            attachmentCount: result.attachmentCount || 0,
            billNumber: result.billNumber,
            warrantyProduct: result.warrantyProduct || {},
            warrantyProductId: result.warrantyProductId || null,
            createdAt: convertToDateSlash(result.createdAt),
            previousStoreName: result.previousStoreName,
          }

          if (Array.isArray(result.warrantyRepairs) && result.warrantyRepairs.length) {
            this.warrantyRepairs = result.warrantyRepairs.map((r) => normalizeWarrantyRepairFromApi(r))
          } else {
            this.warrantyRepairs = [{
              errorGroupId: null,
              solutionId: null,
              errorSeverityId: null,
            }]
          }
          const payments = result.payments || []
          const typeToKey = {
            [PAYMENT_TYPE.INSTALLMENT]: 'installments',
            [PAYMENT_TYPE.CREDIT]: 'credits',
            [PAYMENT_TYPE.TRANSFER]: 'transfer',
            [PAYMENT_TYPE.CASH]: 'cash',
            [PAYMENT_TYPE.REFUND]: 'refunds',
            [PAYMENT_TYPE.DEPOSIT]: 'deposit',
          }
          const grouped = payments.reduce(
            (acc, item) => {
              const key = typeToKey[item.paymentType]
              if (key) acc[key].push(item)
              return acc
            },
            {
              installments: [],
              credits: [],
              transfer: [],
              cash: [],
              refunds: [],
              deposit: [],
            },
          )
          Object.assign(this.formModel, grouped)
          await this.fetchReplaceProducts()
        }
      } catch (error) {
        handleError(error)
      } finally {
        this.isLoadingComponent = false
      }
    },
    async saveWarranty(updateEntityData = {}) {
      this.isLoading = true
      try {
        if (this.isEditMode) {
          const payload = {
            status: this.formModel.status,
            customerId: this.formModel.customerId,
            customerName: this.formModel.customerName,
            customerPhone: this.formModel.customerPhone,
            customerContactNumber: this.formModel.customerContactNumber,
            customerAddress: this.formModel.customerAddress,
            customerEmail: this.formModel.customerEmail,
            productId: this.formModel.productId,
            productName: this.formModel.productName,
            imeiCode: this.formModel.imeiCode,
            purchaseDate: formatDBDate(this.formModel.purchaseDate),
            storeId: this.formModel.storeId,
            warrantyServiceId: this.formModel.warrantyServiceId,
            inspectedByEmployeeId: this.formModel.inspectedByEmployeeId,
            expectedReturnDate: formatDBDate(
              this.formModel.expectedReturnDate,
              CLIENT_DATE_TIME_NO_SECONDS,
              DATETIME_DB_FORMAT,
            ),
            estimatedCost: unMaskPrice(this.formModel.estimatedCost || 0),
            actualCost: unMaskPrice(this.formModel.actualCost || 0),
            supportCost: unMaskPrice(this.formModel.supportCost || 0),
            depositAmount: unMaskPrice(this.formModel.depositAmount || 0) || null,
            issueDescription: this.formModel.issueDescription,
            externalNote: this.formModel.externalNote,
            internalNote: this.formModel.internalNote,
            receiptNumber: this.formModel.receiptNumber,
            trackingNumber: this.formModel.trackingNumber,
            accompanyingEquipments: this.formModel.accompanyingItems.map(
              (accompanyingEquipmentId) => ({
                accompanyingEquipmentId,
              }),
            ),
            warrantyRepairs: this.buildWarrantyRepairsPayload(),
            returnCount: this.formModel.returnCount,
            warrantyProductId: this.formModel.warrantyProductId ?? null,
            ...this.getWarrantyPaymentPayload(),
            ...updateEntityData,
          }
          await updateDispatch(this.formModel.id, payload)
        }
        this.$router.push({
          name: 'warranty-dispatches',
        })
      } finally {
        this.isLoading = false
      }
    },
    async onCompleteWarranty() {
      if (!this.formModel || !this.formModel.id) return

      this.$refs.modalConfirmComplete.show();
    },
    onConfirmCompleteWarranty() {
      this.$nextTick(() => {
        this.saveWarranty({ status: WARRANTY_RECEPTION_STATUS.COMPLETED })
      })
    },
    getWarrantyPaymentPayload() {
      const m = this.formModel
      return {
        cash: m.cash || [],
        transfer: m.transfer || [],
        credits: m.credits || [],
        installments: m.installments || [],
        refunds: m.refunds || [],
        deposit: m.deposit || [],
      }
    },
    resetForm() {
      this.formModel = {
        id: null,
        code: '',
        warrantyServiceId: null,
        warrantyServiceName: '',
        customerId: null,
        customerName: '',
        storeId: null,
        storeName: '',
        previousStoreName: null,
        status: null,
        customerPhone: '',
        customerContactNumber: '',
        customerAddress: '',
        customerEmail: '',
        productId: '',
        productName: '',
        imeiCode: '',
        purchaseDate: null,
        inspectedByEmployeeId: null,
        expectedReturnDate: null,
        estimatedCost: 0,
        actualCost: null,
        supportCost: null,
        repairCost: null,
        issueDescription: '',
        externalNote: '',
        internalNote: '',
        receiptNumber: '',
        trackingNumber: '',
        accompanyingItems: [],
        // Repair fields
        errorGroupId: null,
        technicalErrorId: null,
        technicalErrorIds: null,
        technicalErrors: [],
        errorCode: '',
        errorSeverityId: null,
        primaryTechnicianId: null,
        supportTechnician1Id: null,
        supportTechnician2Id: null,
        startAt: null,
        estimatedCompletionAt: null,
        actualCompletionAt: null,
        solutionId: null,
        repairSolution: '',
        repairMethod: '',
        isCannotRepair: false,
        isDisagreePrice: false,
        newProductId: null,
        newImeiId: null,
        newProductName: null,
        newProductPrice: null,
        newImeiCode: null,
        replaceProducts: [],
        warrantyProduct: {},
        warrantyProductId: null,
        depositAmount: null,
      }
      this.inspectedByEmployeeName = ''
      this.warrantyRepairs = [{}]
      this.technicalErrorOptions = []
    },
    async openOutsourceForm() {
      const hasActive = await checkHasActiveOutsource(this.formModel?.id)
      if (hasActive) {
        makeToastFaile(
          'Máy này đang có phiếu outsource chưa hoàn thành. Vui lòng nhận hàng hoặc huỷ phiếu trước khi tạo mới.',
        )
        return
      }
      this.$refs.outsourceFormRef?.show()
    },
    onOutsourceCreated() {
      this.getWarrantyInfo()
    },
    cancel() {
      this.$router.push({ name: 'warranty-dispatches', query: this.returnQuery })
    },
  },
}
</script>

<style lang="scss" scoped>
.warranty-info {
  .bootstrap-datetimepicker-widget.dropdown-menu {
    width: 21em;
  }
}
</style>
